<template>
  <div class="schedule-config-step">
    <div class="text-body-2 text--secondary mb-4">
      Configure how often data should be automatically pulled from the API.
    </div>

    <!-- Frequency Selection -->
    <v-radio-group
      v-model="schedule.frequency"
      @change="onFrequencyChange"
      class="mt-0"
    >
      <v-radio
        v-for="option in ScheduleFrequencyOptions"
        :key="option.value"
        :value="option.value"
        class="mb-2"
      >
        <template v-slot:label>
          <div class="d-flex align-center">
            <v-icon small class="mr-2">{{ option.icon }}</v-icon>
            <span>{{ option.text }}</span>
          </div>
        </template>
      </v-radio>
    </v-radio-group>

    <!-- Hourly: minute selection -->
    <v-expand-transition>
      <v-row v-if="schedule.frequency === ScheduleFrequency.Hourly" dense class="ml-8">
        <v-col cols="6" sm="4">
          <v-select
            v-model="schedule.minute"
            :items="minuteOptions"
            label="At minute"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Daily: time selection -->
    <v-expand-transition>
      <v-row v-if="schedule.frequency === ScheduleFrequency.Daily" dense class="ml-8">
        <v-col cols="4" sm="3">
          <v-select
            v-model="schedule.hour"
            :items="hourOptions"
            label="Hour"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
        <v-col cols="4" sm="3">
          <v-select
            v-model="schedule.minute"
            :items="minuteOptions"
            label="Minute"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Weekly: day + time -->
    <v-expand-transition>
      <v-row v-if="schedule.frequency === ScheduleFrequency.Weekly" dense class="ml-8">
        <v-col cols="5" sm="4">
          <v-select
            v-model="schedule.dayOfWeek"
            :items="DayOfWeekOptions"
            label="Day"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="schedule.hour"
            :items="hourOptions"
            label="Hour"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="schedule.minute"
            :items="minuteOptions"
            label="Minute"
            outlined
            dense
            @change="emitSchedule"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Custom Cron -->
    <v-expand-transition>
      <div v-if="schedule.frequency === ScheduleFrequency.Custom" class="ml-8">
        <v-text-field
          v-model="schedule.cronExpression"
          label="Cron Expression"
          placeholder="0 */6 * * *"
          outlined
          dense
          class="code-font"
          @input="emitSchedule"
        >
          <template v-slot:append>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
              </template>
              <div class="text-caption" style="max-width: 280px">
                <div class="font-weight-medium mb-1">Cron Format: min hour day month weekday</div>
                <div>*/15 * * * * = Every 15 min</div>
                <div>0 */6 * * * = Every 6 hours</div>
                <div>0 8 * * 1-5 = Weekdays at 8am</div>
                <div>0 0 1 * * = First of month</div>
              </div>
            </v-tooltip>
          </template>
        </v-text-field>
      </div>
    </v-expand-transition>

    <!-- Import on Save checkbox -->
    <v-divider class="my-4" />

    <v-checkbox
      v-model="schedule.triggerImmediate"
      label="Run first import immediately after saving"
      hide-details
      class="mt-0"
      @change="emitSchedule"
    />

    <!-- Schedule Summary -->
    <v-alert
      v-if="scheduleSummary"
      type="info"
      outlined
      dense
      class="mt-4"
    >
      <v-icon slot="prepend" small>mdi-clock-outline</v-icon>
      {{ scheduleSummary }}
    </v-alert>
  </div>
</template>

<script>
import {
  ScheduleFrequency,
  ScheduleFrequencyOptions,
  DayOfWeekOptions,
} from '@/features/Share/config/apiDataSourceEnums';

export default {
  name: 'SharedScheduleConfigStep',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ScheduleFrequency,
      ScheduleFrequencyOptions,
      DayOfWeekOptions,
      schedule: { ...this.value },
    };
  },
  computed: {
    hourOptions() {
      return Array.from({ length: 24 }, (_, i) => ({
        text: `${String(i).padStart(2, '0')}:00`,
        value: i,
      }));
    },
    minuteOptions() {
      return [0, 5, 10, 15, 20, 30, 45].map(m => ({
        text: `:${String(m).padStart(2, '0')}`,
        value: m,
      }));
    },
    scheduleSummary() {
      const f = this.schedule.frequency;
      const h = String(this.schedule.hour).padStart(2, '0');
      const m = String(this.schedule.minute).padStart(2, '0');

      if (f === ScheduleFrequency.Manual) {
        return 'Data will only be pulled when triggered manually.';
      }
      if (f === ScheduleFrequency.Hourly) {
        return `Data will be pulled every hour at :${m} past the hour.`;
      }
      if (f === ScheduleFrequency.Daily) {
        return `Data will be pulled daily at ${h}:${m} UTC.`;
      }
      if (f === ScheduleFrequency.Weekly) {
        const day = DayOfWeekOptions.find(d => d.value === this.schedule.dayOfWeek);
        return `Data will be pulled every ${day ? day.text : ''} at ${h}:${m} UTC.`;
      }
      if (f === ScheduleFrequency.Custom && this.schedule.cronExpression) {
        return `Custom schedule: ${this.schedule.cronExpression}`;
      }
      return '';
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.schedule = { ...newVal };
      },
    },
  },
  methods: {
    onFrequencyChange() {
      this.emitSchedule();
    },
    emitSchedule() {
      this.$emit('input', { ...this.schedule });
    },
  },
};
</script>

<style scoped>
.code-font {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
}
</style>
